import { Typography } from "@material-ui/core";
import clsx from "clsx";
import React from "react";
import { t } from "ttag";
import { symbols } from "../../constants/sensorsUnitsSymbols";
import { HotMode, Unit } from "../../icons";
import { useStoreState } from "../../models/RootStore";
import { getSensorValue } from "../../services/sensorServices";
import { useBacnetUniversalControls } from "../../screens/Control/ControlComponents/ControlItem/hooks/useBacnetUniversalControls";
import { useModbusUniversalControls } from "../../screens/Control/ControlComponents/ControlItem/hooks/useModbusUniversalControls";
import { AutoMode, AutoVRMode, ColdMode, DisconnectIcon, DryIcon, FanMode } from "../../svgComponents";
import useStyles from "./style";

const EMPTY_BACNET_ENTRY: any = {};
const EMPTY_MODBUS_ENTRY: any = {};

const operationModesIcons: any = {
  COOL: <ColdMode white />,
  HEAT: <HotMode white />,
  AUTO: <AutoMode />,
  "Virtual Auto": <AutoVRMode />,
  DRY: <DryIcon />,
  FAN: <FanMode />,
  HAUX: <Typography component="span" style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>HAUX</Typography>,
  HH: <Typography component="span" style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>HH</Typography>,
};

const FloorPlanItem = (props: any) => {
  const classes: any = useStyles();
  const {
    unitId,
    x,
    y,
    width = 120,
    height = 65,
    onClick,
    isEdit = false,
    isSelected = false,
    isConfig = false,
    floorPlans = {},
    bacnetUnits = {},
    modbusUnits = {},
    siteUiTemplates,
    siteParamTemplates,
    isReposition = false,
    isMoving = false,
    type = "unit",
    ...restProps
  } = props;

  const allUnits = useStoreState((state) => state.units.allUnits);
  const allGroups = useStoreState((state) => state.groups.allGroups);
  const allSensors = useStoreState((state) => state.sensors.allSensors);
  const allDevices = useStoreState((state) => state.devices.allDevices);
  const { types, operationStatusesMirror } = useStoreState((state) => state);
  const { operationStatuses, operationModesExtended, sensorTypes } = types;

  const { on: powerOnVal } = operationStatusesMirror;


  const unit: any = allUnits[unitId] || allGroups[unitId];
  const isGroup = !!allGroups[unitId];
  const floorPlanLink = floorPlans[unitId];
  const sensor = allSensors[unitId];
  const isSensor = !!sensor;
  const bacnetUnit = bacnetUnits[unitId];
  const isBacnetUnit = !!bacnetUnit;
  const bacnet = useBacnetUniversalControls(bacnetUnit || EMPTY_BACNET_ENTRY, siteUiTemplates, siteParamTemplates);
  const modbusUnit = modbusUnits[unitId];
  const isModbusUnit = !!modbusUnit;
  const modbus = useModbusUniversalControls(modbusUnit || EMPTY_MODBUS_ENTRY);
  const isNew = !unitId || (!unit && !floorPlanLink && !sensor && !bacnetUnit && !modbusUnit);

  const {
    ambientTemperature,
    activeSetpoint,
    activeOperationMode,
    activeOperationStatus,
    name,
    isVisible
  } = unit || {};

  const device = (unit && allDevices[unit.device]) || (sensor && allDevices[sensor.device]);
  const unitSensorDisconnected = (unit && !unit?.isConnected) || (sensor && sensor?.isConnected === false);

  const mode: any = operationModesExtended[activeOperationMode];

  const onUnitsCount = isGroup ?
    unit?.units.filter((id: any) => {
      const unitObj = allUnits[id];
      if (!unitObj) {
        return false;
      }

      const isUnitOn = unitObj?.activeOperationStatus === +powerOnVal;
      const isUnitConnected = unitObj?.isConnected;
      const isDeviceConnected = allDevices[unitObj?.device]?.isConnected;
      return isUnitOn && isUnitConnected && isDeviceConnected;
    }).length
    + unit?.sensors.filter((id: any) => {
      const sensor = allSensors[id];

      return sensor?.type === 129 && sensor?.readingValue === 1 && allDevices[sensor?.device]?.isConnected;
    }).length :
    0;


  const isBacnetDisconnected = isBacnetUnit && (!bacnetUnit?.isConnected || !allDevices[bacnetUnit?.device]?.isConnected);
  const isModbusDisconnected = isModbusUnit && (!modbusUnit?.isConnected || !allDevices[modbusUnit?.device]?.isConnected);
  const isDisconnected = isBacnetUnit ?
    isBacnetDisconnected :
    (isModbusUnit ?
      isModbusDisconnected :
      (!floorPlanLink && !isNew && !isGroup && (unitSensorDisconnected || !device?.isConnected)));
  const isUnitOn = !isSensor && !isDisconnected && operationStatuses[activeOperationStatus] === "on";
  const isPowerOn = isGroup ? onUnitsCount : isUnitOn;
  const bacnetIsReady = isBacnetUnit && bacnet.isReady && !isDisconnected;
  const modbusIsReady = isModbusUnit && modbus.isReady && !isDisconnected;
  const isHeat = isBacnetUnit ? (bacnetIsReady && bacnet.isStatusHeat) : (isModbusUnit ? (modbusIsReady && modbus.isStatusHeat) : (isPowerOn && mode === "HEAT"));
  const isCool = isBacnetUnit ? (bacnetIsReady && bacnet.isStatusCool) : (isModbusUnit ? (modbusIsReady && modbus.isStatusCool) : (isPowerOn && mode === "COOL"));
  const isOther = isBacnetUnit ? (bacnetIsReady && bacnet.isOn && !isHeat && !isCool) : (isModbusUnit ? (modbusIsReady && modbus.isOn && !isHeat && !isCool) : (isPowerOn && !isCool && !isHeat));
  const modeIcon: any = isGroup ?
    <Unit color={"#fff"} size={width / 3} /> :
    (isDisconnected ? <DisconnectIcon /> : (operationModesIcons[mode] || <Unit color={"#fff"} size={width / 3} />));
  const bacnetVisibleModes = ["HEAT", "COOL", "FAN"];
  const bacnetModeIcon: any = isDisconnected ?
    <DisconnectIcon /> :
    (bacnetVisibleModes.includes(bacnet.modeText) ? operationModesIcons[bacnet.modeText] : null);
  const modbusModeIcon: any = isDisconnected ?
    <DisconnectIcon /> :
    (bacnetVisibleModes.includes(modbus.modeText) ? operationModesIcons[modbus.modeText] : null);

  if ((unitId && !unit && !sensor && !floorPlanLink && !bacnetUnit && !modbusUnit) || (unit && !isGroup && !isVisible)) {
    return null;
  }

  const selectionType = type === "sensor" ? "Sensor" : (type === "link" ? "Link" : "Unit/Group");
  const sensorValue = isSensor && getSensorValue(true, types, sensor);
  const bacnetDisplayName = bacnetUnit?.title || bacnetUnit?.name || bacnetUnit?.id;
  const modbusDisplayName = modbusUnit?.title || modbusUnit?.name || modbusUnit?.id;

  const sensorType = sensorTypes[sensor?.type] || {};
  const { measurementUnits, enableMeasurementUnitSelection } = sensorType;
  const activeMeasurementUnit = enableMeasurementUnitSelection && sensor?.userData?.measurementUnitsType ? sensor?.userData?.measurementUnitsType : measurementUnits;
  const sensorUnit = symbols[activeMeasurementUnit] || "";


  const groupSetpoint = isGroup && unit?.units?.reduce((data: any, uId: string) => {
    const unit = allUnits[uId] || null
    if (!unit) {
      return data;
    }
    if (data === -1) {
      return Math.ceil(unit.activeSetpoint || 0);
    }

    if (data !== Math.ceil(unit.activeSetpoint || 0)) {
      return null;
    }

    return data;
  }, -1)

  const renderBacnetInfo = () => (
    <>
      <Typography align="center" className={classes.itemName}>{bacnetDisplayName}</Typography>
      {(bacnetIsReady || bacnetModeIcon) &&
        <div className={classes.infoSection}>
          {bacnetModeIcon &&
            <div className={clsx(classes.iconContainer, { [classes.iconFullWidthContainer]: !bacnetIsReady })}>
              {bacnetModeIcon}
            </div>}
          {bacnetIsReady &&
            <div className={classes.infoContainer}>
              <span
                className={classes.unitTemp}
                style={{
                  fontSize: `16px`
                }}
              >
                {bacnet.isOn ?
                  (bacnet.showSetpoint && typeof bacnet.currentSetpoint === "number" ? `${Math.round(bacnet.currentSetpoint)}${bacnet.tempUnit}` : t`ON`) :
                  t`OFF`}
              </span>
              {bacnet.roomTempKey && typeof bacnet.currentRoomTemp === "number" &&
                <span
                  className={classes.roomTemp}
                  style={{ fontSize: `13px` }}
                >
                  {t`Now`}{`: ${Math.round(bacnet.currentRoomTemp)}${bacnet.tempUnit}`}
                </span>}
            </div>}
        </div>}
    </>
  );

  const renderModbusInfo = () => (
    <>
      <Typography align="center" className={classes.itemName}>{modbusDisplayName}</Typography>
      {(modbusIsReady || modbusModeIcon) &&
        <div className={classes.infoSection}>
          {modbusModeIcon &&
            <div className={clsx(classes.iconContainer, { [classes.iconFullWidthContainer]: !modbusIsReady })}>
              {modbusModeIcon}
            </div>}
          {modbusIsReady &&
            <div className={classes.infoContainer}>
              <span
                className={classes.unitTemp}
                style={{
                  fontSize: `16px`
                }}
              >
                {modbus.isOn ?
                  (modbus.showSetpoint && typeof modbus.currentSetpoint === "number" ? `${Math.round(modbus.currentSetpoint)}${modbus.tempUnit}` : t`ON`) :
                  t`OFF`}
              </span>
              {modbus.roomTempKey && typeof modbus.currentRoomTemp === "number" &&
                <span
                  className={classes.roomTemp}
                  style={{ fontSize: `13px` }}
                >
                  {t`Now`}{`: ${Math.round(modbus.currentRoomTemp)}${modbus.tempUnit}`}
                </span>}
            </div>}
        </div>}
    </>
  );

  return (
    <div
      className={clsx(
        classes.container,
        isNew && classes.isNewUnit,
        isDisconnected && classes.disconnected,
        isHeat && classes.isHeat,
        isCool && classes.isCool,
        isOther && classes.isOther,
        isEdit && classes.isEdit,
        isSelected && classes.isSelected
      )}
      style={{
        zIndex: 200,
        left: `${x}%`,
        top: `${y}%`,
        position: "absolute",
        width,
        height,
        boxSizing: "content-box",
        cursor: isEdit && isReposition && !isMoving ? "grab" : (isEdit && isReposition && isMoving ? "grabbing" : "pointer")
      }}
      onClick={(e: any) => onClick(e, isDisconnected)}
      {...restProps}
    >

      {isNew ?
        <Typography style={{ color: "#fff", fontSize: "14px" }} align="center">{isConfig ? t`Select ${selectionType}` : t`Drag and click to position ${selectionType}`}</Typography> :
        <div
          style={{
            zIndex: 200,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: !!floorPlanLink ? "row" : "column",
            justifyContent: !!floorPlanLink ? "space-evenly" : "center",
            alignItems: "center"
          }}
        >
          {!!floorPlanLink ?
            <Typography
              align="center"
              className={classes.linkText}
            >
              {floorPlanLink?.name}
            </Typography> :
            (
              isSensor ?
                <>
                  <Typography align="center" style={{
                    fontSize: `16px`,
                    fontWeight: "bold",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%"
                  }}>{sensor.name}</Typography>
                  <div className={classes.infoSection} style={{ justifyContent: "center" }}>
                    <span
                      className={classes.unitTemp}
                      style={{
                        fontSize: `16px`
                      }}
                    >
                      {`${sensorValue} ${sensorUnit}`}
                    </span>
                  </div>
                </>
                :
                (isBacnetUnit ?
                  renderBacnetInfo()
                  :
                (isModbusUnit ?
                  renderModbusInfo()
                  :
                <>
                  <Typography align="center" style={{
                    fontSize: `16px`,
                    fontWeight: "bold",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%"
                  }}>{name}</Typography>
                  <div className={classes.infoSection}>
                    <div className={classes.iconContainer}>
                      {modeIcon}
                    </div>
                    <div className={classes.infoContainer}>
                      {isGroup ?
                        <span
                          className={classes.unitTemp}
                          style={{
                            fontSize: `16px`
                          }}
                        >
                          {isPowerOn ? `${Math.round(groupSetpoint) || "--"}°` : t`OFF`}
                        </span> :
                        <span
                          className={classes.unitTemp}
                          style={{
                            fontSize: `16px`
                          }}
                        >
                          {isPowerOn || isNew ? `${Math.round(activeSetpoint as any) || "--"}°` : t`OFF`}
                        </span>
                      }
                      {!isGroup && <span
                        className={classes.roomTemp}
                        style={{ fontSize: `13px` }}
                      >
                        {t`Now`}{`: ${ambientTemperature || "--"}°`}
                      </span>}
                    </div>
                  </div>
                </>))
            )
          }
        </div>}
    </div>
  );
};

export default FloorPlanItem;
